import { parseSupport } from './assistant-support-validation';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, TimeoutError, filter, firstValueFrom } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import { httpChatFrames } from './assistant-http-stream';
import { ASSISTANT_API_CONFIG } from './assistant.config';
import { isAssistantNavigationTarget } from './assistant-navigation';
import type {
  AssistantCitation,
  AssistantEvent,
  AssistantRequest,
  AssistantWireRequest,
  AssistantSupportCredentials,
  AssistantSupportResult,
} from './assistant.models';

export class AssistantUnavailableError extends Error {}

/** Injectable HTTP frame source; production always uses authenticated POST SSE. */
export const ASSISTANT_HTTP_FRAMES = new InjectionToken<typeof httpChatFrames>('Assistant HTTP frames', {
  providedIn: 'root', factory: () => httpChatFrames,
});

/** Only approved categories cross the transport boundary; raw server messages never do. */
export class AssistantApiError extends Error {
  constructor(readonly reason: 'timeout' | 'busy' | 'unavailable' | 'retrieval' | 'invalid' | 'session') {
    super('Assistant request failed');
  }
}

/** Validates assistant events from the HTTP streaming transport. */
@Injectable({ providedIn: 'root' })
export class HubCentralChatApiService {
  private readonly config = inject(ASSISTANT_API_CONFIG);
  private readonly auth = inject(OKTA_AUTH, { optional: true });
  private readonly httpFrames = inject(ASSISTANT_HTTP_FRAMES);

  async feedback(sessionId: string, context: AssistantRequest['context'],
    command: NonNullable<AssistantRequest['feedbackCommand']>) {
    const event = await firstValueFrom(this.send({ question: '', sessionId,
      requestId: crypto.randomUUID(), context, feedbackCommand: command,
    }).pipe(filter(event => event.type === 'feedback_completed')));
    return event.feedback;
  }

  async support(action: 'startSupport' | 'endSupport', sessionId: string, handoffId: string,
    context: AssistantRequest['context']): Promise<AssistantSupportResult> {
    const event = await firstValueFrom(this.send({
      question: '', sessionId, requestId: crypto.randomUUID(), context,
      supportCommand: { action, handoffId },
    }).pipe(filter(event => event.type === 'support_completed')));
    return event.support;
  }

  /** One connection per question; backend history uses the session ID across connections when enabled.
   * Cancellation stops local delivery; it does not guarantee server-side execution stops.
   * No automatic retries.
   */
  send(request: AssistantRequest): Observable<AssistantEvent> {
    return new Observable((subscriber) => {
      const httpUrl = this.config.httpUrl;
      if (!httpUrl) {
        subscriber.error(new AssistantUnavailableError());
        return;
      }
      const payload: AssistantWireRequest | Record<string, unknown> = request.feedbackCommand ? {
        action: request.feedbackCommand.action, answer_id: request.feedbackCommand.answerId,
        vote: request.feedbackCommand.vote ?? null, comment: request.feedbackCommand.comment ?? '',
        revision: request.feedbackCommand.revision ?? 0,
        session_id: request.sessionId, request_id: request.requestId,
        partner_id: request.context.partnerId, roles: request.context.roles,
      } : request.supportCommand ? {
        action: request.supportCommand.action, handoff_id: request.supportCommand.handoffId,
        session_id: request.sessionId, request_id: request.requestId,
        partner_id: request.context.partnerId, roles: request.context.roles,
      } : {
        action: 'sendMessage',
        message: request.question,
        session_id: request.sessionId,
        request_id: request.requestId,
        partner_id: request.context.partnerId,
        roles: request.context.roles,
      };
      const encoded = JSON.stringify(payload);
      if (new TextEncoder().encode(encoded).length > this.config.maxFrameBytes) {
        subscriber.error(new Error('Request exceeds transport limit'));
        return;
      }
      const timer = setTimeout(
        () => subscriber.error(new TimeoutError()),
        this.config.requestTimeoutMs,
      );
      let answerLength = 0;
      let sessionId = request.sessionId;
      const fail = () => subscriber.error(new Error('Assistant transport failed'));
      const receive = (data: unknown) => {
        try {
          if (
            typeof data !== 'string' ||
            new TextEncoder().encode(data).length > this.config.maxFrameBytes
          )
            throw new Error('Invalid frame');
          const event: unknown = JSON.parse(data);
          if (!isRecord(event) || typeof event['request_id'] !== 'string')
            throw new Error('Missing correlation ID');
          // A late or unrelated response must never appear under the current question.
          if (event['request_id'] !== request.requestId) return;
          if (event['session_id'] !== undefined) {
            const incoming = event['session_id'];
            if (
              typeof incoming !== 'string' ||
              !incoming.trim() ||
              incoming.length > 256 ||
              (sessionId !== null && incoming !== sessionId)
            )
              throw new Error('Invalid session');
            sessionId = incoming;
          }
          if (request.feedbackCommand && !['feedback_completed', 'error', 'step'].includes(String(event['type'])))
            throw new Error('Unexpected feedback response');
          switch (event['type']) {
            case 'feedback_completed': {
              const feedback = event['feedback'];
              if (!request.feedbackCommand || !sessionId || !isRecord(feedback) ||
                  ![null, 'up', 'down'].includes(feedback['vote'] as null | string) ||
                  typeof feedback['comment'] !== 'string' || feedback['comment'].length > 1000 ||
                  (feedback['vote'] !== 'down' && feedback['comment'] !== '') ||
                  !Number.isSafeInteger(feedback['revision']) || (feedback['revision'] as number) < 0)
                throw new Error('Invalid feedback response');
              subscriber.next({ type: 'feedback_completed', sessionId, feedback: {
                vote: feedback['vote'] as 'up' | 'down' | null,
                comment: feedback['comment'], revision: feedback['revision'] as number,
              } });
              subscriber.complete();
              break;
            }
            case 'support_pending': {
              if (request.supportCommand || !sessionId || event['handoff_id'] !== request.requestId)
                throw new Error('Invalid support attempt');
              subscriber.next({ type: 'support_pending', sessionId, handoffId: request.requestId });
              break;
            }
            case 'support_completed': {
              if (!request.supportCommand || !sessionId) throw new Error('Unexpected support response');
              const support = parseSupport(event['support']);
              if (support.handoffId !== request.supportCommand.handoffId ||
                  (request.supportCommand.action === 'endSupport') !== ('ended' in support))
                throw new Error('Mismatched support response');
              subscriber.next({ type: 'support_completed', sessionId, support });
              subscriber.complete();
              break;
            }
            case 'chunk': {
              const text = event['text'];
              if (typeof text !== 'string') throw new Error('Invalid chunk');
              answerLength += text.length;
              if (answerLength > this.config.maxAnswerCharacters)
                throw new Error('Answer too large');
              subscriber.next({ type: 'chunk', text });
              break;
            }
            case 'completed': {
              if (request.supportCommand) throw new Error('Unexpected assistant response');
              const answer = event['answer'];
              if (
                typeof answer !== 'string' ||
                !answer.trim() ||
                answer.length > this.config.maxAnswerCharacters ||
                !sessionId
              )
                throw new Error('Invalid completion');
              const status = event['status'];
              if (event['incomplete'] !== undefined && typeof event['incomplete'] !== 'boolean')
                throw new Error('Invalid completion flag');
              if (
                status !== 'answered' &&
                status !== 'not_found' &&
                status !== 'refused' &&
                status !== 'conversation' &&
                status !== 'clarification' &&
                status !== 'out_of_scope' &&
                status !== 'navigation'
              )
                throw new Error('Invalid response status');
              const citations = parseCitations(event['citations']);
              if (status === 'answered' && citations.length === 0)
                throw new Error('Missing answer sources');
              if (
                ['conversation', 'clarification', 'out_of_scope', 'navigation'].includes(status) &&
                citations.length
              )
                throw new Error('Unexpected conversational sources');
              const targets: unknown = event['navigation_ids'];
              if (status === 'navigation' && (!Array.isArray(targets) || targets.length === 0))
                throw new Error('Missing navigation');
              if (
                targets !== undefined &&
                (!Array.isArray(targets) ||
                  targets.length > 3 ||
                  !targets.every(isAssistantNavigationTarget) ||
                  (['conversation', 'clarification', 'out_of_scope'].includes(status) &&
                    targets.length))
              )
                throw new Error('Invalid navigation');
              const handoff = event['handoff'];
              if (handoff !== undefined && handoff !== null &&
                  (handoff !== 'live_agent' || status !== 'conversation' || citations.length || (Array.isArray(targets) && targets.length)))
                throw new Error('Invalid handoff');
              let support: AssistantSupportCredentials | undefined;
              if (event['support'] !== undefined) {
                const parsed = parseSupport(event['support']);
                if (handoff !== 'live_agent' || 'ended' in parsed || parsed.handoffId !== request.requestId)
                  throw new Error('Invalid handoff credentials');
                support = parsed;
              }
              // Older backends may omit the optional field during a staged rollout.
              const navigation =
                targets === undefined
                  ? {}
                  : {
                      navigationIds: [
                        ...new Set(
                          targets as import('./assistant-navigation').AssistantNavigationTarget[],
                        ),
                      ],
                    };
              subscriber.next({
                type: 'completed',
                ...(event['incomplete'] === true ? { incomplete: true } : {}),
                ...(event['feedback_available'] === true ? { feedbackAvailable: true } : {}),
                answer,
                sessionId,
                status,
                citations,
                ...navigation,
                ...(handoff === 'live_agent' ? { handoff: 'live_agent' as const } : {}),
                ...(support ? { support } : {}),
              });
              subscriber.complete();
              break;
            }
            case 'error':
              subscriber.error(new AssistantApiError(safeErrorReason(event['code'])));
              break;
            case 'step':
            case 'thinking':
              break; // Existing protocol compatibility; never render agent reasoning/traces.
            default:
              throw new Error('Unknown event');
          }
        } catch {
          fail();
        }
      };
      const httpSubscription = this.httpFrames(
            httpUrl, encoded, this.auth?.getAccessToken(), this.config.maxFrameBytes,
          ).subscribe({
            next: receive,
            error: fail,
            complete: () => {
              if (!subscriber.closed) fail(); // EOF without a validated completion is an error.
            },
          });
      return () => {
        clearTimeout(timer);
        httpSubscription.unsubscribe();
      };
    });
  }

}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function safeErrorReason(code: unknown): AssistantApiError['reason'] {
  switch (code) {
    case 'REQUEST_BUDGET_EXCEEDED':
      return 'timeout';
    case 'ASSISTANT_BUSY':
      return 'busy';
    case 'SESSION_UNAVAILABLE':
      return 'session';
    case 'INVALID_REQUEST':
      return 'invalid';
    case 'RETRIEVAL_UNAVAILABLE':
      return 'retrieval';
    default:
      return 'unavailable';
  }
}

/** Citations are text-only labels, never storage URLs or executable model-generated links. */
function parseCitations(value: unknown): readonly AssistantCitation[] {
  if (!Array.isArray(value) || value.length > 20) throw new Error('Invalid sources');
  const ids = new Set<string>();
  return value.map((item: unknown) => {
    if (!isRecord(item)) throw new Error('Invalid source');
    const id = item['id'];
    const documentId = item['document_id'];
    const title = item['title'];
    if (
      typeof id !== 'string' ||
      !/^S[1-9][0-9]*$/.test(id) ||
      ids.has(id) ||
      typeof documentId !== 'string' ||
      !/^doc_[a-f0-9]{24}$/.test(documentId) ||
      typeof title !== 'string' ||
      !title.trim() ||
      title.length > 200 ||
      Array.from(title).some(
        (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
      )
    )
      throw new Error('Invalid source');
    ids.add(id);
    return { id, documentId, title };
  });
}
